import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, relTime } from "@/lib/format";
import { toast } from "sonner";
import {
    Share2,
    Copy,
    Trash2,
    Eye,
    Plus,
    ShieldCheck,
    Clock,
    Users,
    Download,
    RefreshCcw,
    X,
    Check,
    Send,
    Mail,
} from "lucide-react";

/**
 * Auditor / CA Share Manager (owner side).
 *
 * Lists all shares the current user has created, lets them:
 *   • Create a new share with scoped period + sections + expiry
 *   • Copy the public link
 *   • Revoke immediately
 *   • Inspect the access log for a share
 */
const ALL_SECTIONS = [
    { key: "financial_position", label: "Financial position" },
    { key: "income", label: "Income summary" },
    { key: "expenses", label: "Expense summary" },
    { key: "tax", label: "Tax records" },
    { key: "capital_purchases", label: "Capital purchases" },
    { key: "reconciliation", label: "Reconciliation status" },
    { key: "documents", label: "Documents (metadata)" },
    { key: "missing_records", label: "Missing records" },
];

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function addDaysISO(n) {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
}

export default function AuditorShares() {
    const [shares, setShares] = useState([]);
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [accessLog, setAccessLog] = useState(null);
    const [emailing, setEmailing] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/auditor/shares");
            setShares(Array.isArray(r.data) ? r.data : []);
        } catch {
            toast.error("Failed to load shares");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const publicUrl = (token) => `${window.location.origin}/auditor/${token}`;

    async function revoke(id) {
        if (!window.confirm("Revoke this share immediately? The auditor will lose access.")) return;
        try {
            await api.delete(`/auditor/shares/${id}`);
            toast.success("Share revoked");
            load();
        } catch { toast.error("Failed to revoke"); }
    }
    async function viewLog(share) {
        try {
            const r = await api.get(`/auditor/shares/${share.id}/access-log`);
            setAccessLog({ share, logs: Array.isArray(r.data) ? r.data : [] });
        } catch { toast.error("Failed to load access log"); }
    }
    async function copy(token) {
        try {
            await navigator.clipboard.writeText(publicUrl(token));
            toast.success("Link copied");
        } catch { toast.error("Copy failed"); }
    }

    return (
        <div className="max-w-[1200px] mx-auto" data-testid="auditor-shares-page">
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-1 dark:text-stone-400">
                        Sharing
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-stone-900 tracking-tight dark:text-white">
                        Auditor / CA View
                    </h1>
                    <p className="text-sm text-stone-500 mt-1 dark:text-stone-400">
                        Read-only, period-scoped, revocable snapshots of your finances. Card &amp; bank numbers are always masked.
                    </p>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-700 transition-colors dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200"
                    data-testid="create-share-btn"
                >
                    <Plus size={14} /> Create share
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-stone-500">Loading shares…</div>
            ) : shares.length === 0 ? (
                <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 border-dashed rounded-2xl p-10 text-center">
                    <ShieldCheck size={26} className="mx-auto text-stone-400 mb-3" />
                    <p className="text-sm text-stone-600 dark:text-stone-300">
                        No shares yet. Create your first read-only Auditor View to send to your CA.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="shares-grid">
                    {shares.map((s) => {
                        const now = new Date();
                        const exp = new Date(s.expires_at);
                        const revoked = !!s.revoked_at;
                        const expired = !revoked && exp <= now;
                        const active = !revoked && !expired;
                        return (
                            <div
                                key={s.id}
                                className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl p-5"
                                data-testid={`share-card-${s.id}`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <div className="text-sm font-semibold text-stone-900 dark:text-white">{s.label}</div>
                                        <div className="text-[11px] text-stone-500 mt-0.5 dark:text-stone-400">
                                            {s.period_from} → {s.period_to} · {s.sections.length} section(s)
                                        </div>
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full ${
                                        active ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" :
                                        expired ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" :
                                        "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400"
                                    }`}>
                                        {revoked ? "Revoked" : expired ? "Expired" : "Active"}
                                    </span>
                                </div>
                                <div className="mt-3 space-y-1 text-[12px] text-stone-500 dark:text-stone-400">
                                    <div className="flex items-center gap-1.5"><Clock size={12} /> Expires {fmtDate(s.expires_at)} ({relTime(s.expires_at)})</div>
                                    <div className="flex items-center gap-1.5"><Eye size={12} /> {s.access_count || 0} view(s){s.last_accessed_at && ` · last ${relTime(s.last_accessed_at)}`}</div>
                                    {s.auditor_name && <div className="flex items-center gap-1.5"><Users size={12} /> For: {s.auditor_name}{s.auditor_email && ` <${s.auditor_email}>`}</div>}
                                    {s.allow_downloads ? (
                                        <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400"><Download size={12} /> Downloads enabled</div>
                                    ) : (
                                        <div className="flex items-center gap-1.5 text-stone-500"><X size={12} /> Downloads disabled</div>
                                    )}
                                </div>
                                <div className="mt-4 flex items-center gap-2 flex-wrap">
                                    <button
                                        onClick={() => copy(s.token)}
                                        disabled={!active}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 dark:border-stone-700 text-[12px] font-medium text-stone-700 dark:text-stone-200 hover:border-stone-300 disabled:opacity-40"
                                        data-testid={`share-copy-${s.id}`}
                                    >
                                        <Copy size={12} /> Copy link
                                    </button>
                                    <a
                                        href={publicUrl(s.token)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 dark:border-stone-700 text-[12px] font-medium text-stone-700 dark:text-stone-200 hover:border-stone-300"
                                        data-testid={`share-open-${s.id}`}
                                    >
                                        <Eye size={12} /> Open
                                    </a>
                                    <button
                                        onClick={() => viewLog(s)}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 dark:border-stone-700 text-[12px] font-medium text-stone-700 dark:text-stone-200 hover:border-stone-300"
                                        data-testid={`share-log-${s.id}`}
                                    >
                                        <Users size={12} /> Access log
                                    </button>
                                    {active && (
                                        <button
                                            onClick={() => setEmailing(s)}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-200 dark:border-emerald-900 text-[12px] font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                                            data-testid={`share-send-${s.id}`}
                                        >
                                            <Send size={12} /> Send to CA
                                        </button>
                                    )}
                                    {active && (
                                        <button
                                            onClick={() => revoke(s.id)}
                                            className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose-200 text-[12px] font-medium text-rose-700 hover:bg-rose-50 dark:border-rose-900 dark:text-rose-400 dark:hover:bg-rose-950"
                                            data-testid={`share-revoke-${s.id}`}
                                        >
                                            <Trash2 size={12} /> Revoke
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {creating && <CreateShareModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
            {accessLog && <AccessLogModal data={accessLog} onClose={() => setAccessLog(null)} />}
            {emailing && <SendToCAModal share={emailing} onClose={() => setEmailing(null)} onSent={() => { setEmailing(null); load(); }} />}
        </div>
    );
}

function SendToCAModal({ share, onClose, onSent }) {
    const [to, setTo] = useState(share.auditor_email || "");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!to.trim() || !to.includes("@")) { toast.error("Enter a valid email"); return; }
        setBusy(true);
        try {
            const r = await api.post(`/auditor/shares/${share.id}/email`, {
                to: to.trim(),
                message: msg.trim() || null,
            });
            toast.success(`Sent to ${r.data.delivered_to}`);
            onSent();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to send");
        } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
                data-testid="send-to-ca-modal"
            >
                <form onSubmit={submit} className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Send to CA</div>
                            <h2 className="text-lg font-bold text-stone-900 dark:text-white">{share.label}</h2>
                        </div>
                        <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700 dark:hover:text-white">
                            <X size={18} />
                        </button>
                    </div>
                    <p className="text-[13px] text-stone-500 dark:text-stone-400 mb-4">
                        We&rsquo;ll email the tokenised link with expiry, sections shared, and your note. The email is delivered via Resend from your VAULT sender address.
                    </p>
                    <div className="space-y-4">
                        <Field label="Recipient email" required>
                            <div className="relative">
                                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                <input
                                    type="email"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    placeholder="ca@yourfirm.com"
                                    required
                                    data-testid="send-to-email"
                                    className="w-full pl-9 pr-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm focus:outline-none focus:border-stone-400"
                                />
                            </div>
                        </Field>
                        <Field label="Note (optional)">
                            <textarea
                                value={msg}
                                onChange={(e) => setMsg(e.target.value)}
                                rows={4}
                                maxLength={500}
                                placeholder="Any context for your CA — e.g. 'Q4 review, please focus on capital gains.'"
                                data-testid="send-to-message"
                                className="w-full px-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm resize-none focus:outline-none focus:border-stone-400"
                            />
                            <div className="text-[11px] text-stone-400 mt-1">{msg.length}/500</div>
                        </Field>
                    </div>
                    <div className="mt-6 flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-200 dark:border-stone-700 text-sm text-stone-700 dark:text-stone-200 hover:border-stone-300">Cancel</button>
                        <button type="submit" disabled={busy} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="send-to-submit">
                            <Send size={13} /> {busy ? "Sending…" : "Send email"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CreateShareModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        label: "", period_from: addDaysISO(-90), period_to: todayISO(),
        expires_at: `${addDaysISO(14)}T23:59:00`, allow_downloads: true,
        auditor_name: "", auditor_email: "",
    });
    const [sections, setSections] = useState(ALL_SECTIONS.map((s) => s.key));
    const [busy, setBusy] = useState(false);

    const toggle = (k) => setSections((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

    async function submit(e) {
        e.preventDefault();
        if (!form.label.trim()) { toast.error("Label required"); return; }
        if (sections.length === 0) { toast.error("Select at least one section"); return; }
        setBusy(true);
        try {
            await api.post("/auditor/shares", {
                ...form,
                expires_at: new Date(form.expires_at).toISOString(),
                sections,
                entities: ["all"],
            });
            toast.success("Share created");
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create");
        } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="create-share-modal">
                <form onSubmit={submit} className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-stone-900 dark:text-white">Create Auditor Share</h2>
                        <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700 dark:hover:text-white">
                            <X size={18} />
                        </button>
                    </div>
                    <div className="space-y-4">
                        <Field label="Share label" required>
                            <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="FY 2025-26 · Auditor Sharma" required data-testid="share-label"
                                className="w-full px-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm focus:outline-none focus:border-stone-400" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Period from"><input type="date" value={form.period_from} onChange={(e) => setForm({ ...form, period_from: e.target.value })} data-testid="share-from" className="w-full px-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm" /></Field>
                            <Field label="Period to"><input type="date" value={form.period_to} onChange={(e) => setForm({ ...form, period_to: e.target.value })} data-testid="share-to" className="w-full px-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm" /></Field>
                        </div>
                        <Field label="Link expires on">
                            <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="share-expiry" className="w-full px-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm" />
                        </Field>
                        <Field label="Sections to share">
                            <div className="grid grid-cols-2 gap-2">
                                {ALL_SECTIONS.map((s) => (
                                    <label key={s.key} className="flex items-center gap-2 text-[13px] text-stone-700 dark:text-stone-200 cursor-pointer">
                                        <input type="checkbox" checked={sections.includes(s.key)} onChange={() => toggle(s.key)} data-testid={`share-section-${s.key}`} />
                                        <span>{s.label}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Auditor name (optional)"><input type="text" value={form.auditor_name} onChange={(e) => setForm({ ...form, auditor_name: e.target.value })} data-testid="share-auditor-name" className="w-full px-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm" /></Field>
                            <Field label="Auditor email (optional)"><input type="email" value={form.auditor_email} onChange={(e) => setForm({ ...form, auditor_email: e.target.value })} data-testid="share-auditor-email" className="w-full px-3 py-2 border border-stone-200 dark:border-stone-700 dark:bg-stone-800 dark:text-white rounded-lg text-sm" /></Field>
                        </div>
                        <label className="flex items-center gap-2 text-[13px] text-stone-700 dark:text-stone-200">
                            <input type="checkbox" checked={form.allow_downloads} onChange={(e) => setForm({ ...form, allow_downloads: e.target.checked })} data-testid="share-allow-downloads" />
                            Allow auditor to download PDF / Excel
                        </label>
                    </div>
                    <div className="mt-6 flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-stone-200 dark:border-stone-700 text-sm text-stone-700 dark:text-stone-200 hover:border-stone-300">Cancel</button>
                        <button type="submit" disabled={busy} className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-700 disabled:opacity-50 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200" data-testid="share-submit">
                            {busy ? "Creating…" : "Create share"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function AccessLogModal({ data, onClose }) {
    const { share, logs } = data;
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="access-log-modal">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Access log</div>
                            <h2 className="text-lg font-bold text-stone-900 dark:text-white">{share.label}</h2>
                        </div>
                        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 dark:hover:text-white"><X size={18} /></button>
                    </div>
                    {logs.length === 0 ? (
                        <p className="text-sm text-stone-500">This link has not been opened yet.</p>
                    ) : (
                        <ul className="divide-y divide-stone-100 dark:divide-stone-800">
                            {logs.map((l) => (
                                <li key={l.id} className="py-2.5 text-[13px]">
                                    <div className="text-stone-800 dark:text-stone-200">{fmtDate(l.timestamp)} · {relTime(l.timestamp)}</div>
                                    <div className="text-[11px] text-stone-500">{l.ip} · <span className="truncate">{(l.user_agent || "").slice(0, 60)}</span></div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-[12px] font-medium text-stone-700 dark:text-stone-300 mb-1">
                {label} {required && <span className="text-rose-500">*</span>}
            </span>
            {children}
        </label>
    );
}
